import React, { useState } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Paper,
  Typography
} from '@mui/material';
import PageTitle from '../components/layout/PageTitle';
import { useAuth } from '../context/AuthContext';
import { USER_ROLES } from '../types/users';

// Import des composants de paramètres
import UserManagement from '../components/settings/UserManagement';
import PermissionsSettings from '../components/settings/PermissionsSettings';
import EmailSettings from '../components/settings/EmailSettings';
import SmtpSettings from '../components/settings/SmtpSettings';
import ChangePassword from '../components/settings/ChangePassword';
import AISettings from '../components/settings/AISettings';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SettingsPage = () => {
  const { currentUser } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Configuration des onglets selon le rôle
  const getAvailableTabs = () => {
    const tabs = [];

    // Onglets pour Admin et SuperAdmin - Paramètres Email en premier
    if ([USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN].includes(currentUser?.role)) {
      tabs.push({
        label: 'Paramètres Email',
        component: <EmailSettings />,
        roles: [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]
      });
    }

    // Onglets spécifiques SuperAdmin
    if (currentUser?.role === USER_ROLES.SUPER_ADMIN) {
      tabs.push({
        label: 'Gestion des utilisateurs',
        component: <UserManagement />,
        roles: [USER_ROLES.SUPER_ADMIN]
      });

      tabs.push({
        label: 'Permissions de visibilité',
        component: <PermissionsSettings />,
        roles: [USER_ROLES.SUPER_ADMIN]
      });

      tabs.push({
        label: '🤖 Paramètres IA',
        component: <AISettings />,
        roles: [USER_ROLES.SUPER_ADMIN]
      });

      tabs.push({
        label: 'Configuration SMTP',
        component: <SmtpSettings />,
        roles: [USER_ROLES.SUPER_ADMIN]
      });
    }

    // Onglet disponible pour tous les utilisateurs connectés - Changer le mot de passe à la fin
    tabs.push({
      label: 'Changer le mot de passe',
      component: <ChangePassword />,
      roles: ['all']
    });

    return tabs.filter(tab => 
      tab.roles.includes('all') || 
      tab.roles.includes(currentUser?.role)
    );
  };

  const availableTabs = getAvailableTabs();

  if (!currentUser) {
    return (
      <Box>
        <PageTitle title="Paramètres" />
        <Paper sx={{ p: 3 }}>
          <Typography color="error">
            Vous devez être connecté pour accéder aux paramètres.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle title="Paramètres" />
      
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {availableTabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Paper>
      
      {availableTabs.map((tab, index) => (
        <TabPanel key={index} value={currentTab} index={index}>
          {tab.component}
        </TabPanel>
      ))}
    </Box>
  );
};

export default SettingsPage;
